// ---------------------------------------------------------
// Datei: FileUploadModal.tsx
// Zweck: Universelles Upload-Modal für Kamera, Galerie und Dateisystem
// Besonderheiten: 
// - Unterstützt Kamera (wie bisher)
// - Unterstützt Galerie (nur Bilder)
// - Unterstützt Dateisystem (alle Formate inkl. PDF)
// - Multi-Upload für Arbeitsberichte
// - Vorschau für Bilder und PDFs
// Stand: 14.  Januar 2026
// ---------------------------------------------------------

import React, { useState, useRef, useCallback, useEffect } from 'react';
//import { useQueue } from '../contexts/QueueContext';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: UploadFile[]) => void;
  title?:  string;
  /** Erlaubte MIME-Types */
  allowedTypes?: string[];
  /** Multi-Upload erlauben (für Arbeitsberichte) */
  multiUpload?: boolean;
  /** Dokument-ID für Queue */
  documentId?:  string;
  /** Phase-ID für Queue */
  phaseId?: string;
  /** Projekt-ID */
  projectId?: string;
}

export interface UploadFile {
  id: string;
  name: string;
  type: string;
  size: number;
  dataUrl:  string;
  file?:  File;
}

type UploadMode = 'selection' | 'camera' | 'preview';

const FileUploadModal:  React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = 'Dokument hinzufügen',
  allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'],
  multiUpload = false,
  //documentId,
  //phaseId,
  //projectId,
}) => {
  const debugLog = (...args: any[]) => {
    console.log('[debug-upload][FileUploadModal]', ...args);
  };
  // === States ===
  const [mode, setMode] = useState<UploadMode>('selection');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // === Refs ===
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  //const { addToQueue } = useQueue();

  // === Accept-String für File-Input ===
  // Wichtig: Explizit alle erlaubten Typen auflisten
  const acceptString = allowedTypes.join(',');
  
  // Prüfen ob PDFs erlaubt sind
  const pdfAllowed = allowedTypes.includes('application/pdf');
  const imagesAllowed = allowedTypes.some(t => t.startsWith('image/'));

  // === Kamera-Funktionen ===
  const startCamera = useCallback(async () => {
    try {
      debugLog('startCamera: requesting media');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraActive(true);
        debugLog('startCamera: active');
      }
    } catch (err) {
      console.error('Kamera-Zugriff fehlgeschlagen:', err);
      debugLog('startCamera: failed', err);
      setError('Kamerazugriff nicht möglich.  Bitte Berechtigung erteilen.');
      setMode('selection');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef. current?. srcObject) {
      const tracks = (videoRef.current. srcObject as MediaStream).getTracks();
      tracks.forEach(track => track. stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
    }
  }, []);

  const takePicture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    debugLog('takePicture');
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/png'); // Verlustfrei
    const newFile: UploadFile = {
      id:  `camera-${Date.now()}`,
      name: `Foto_${new Date().toISOString().slice(0, 10)}.png`,
      type: 'image/png',
      size: Math.round(dataUrl.length * 0.75), // Approx size from base64
      dataUrl,
    };

    if (multiUpload) {
      setFiles(prev => [...prev, newFile]);
    } else {
      setFiles([newFile]);
    }

    stopCamera();
    setMode('preview');
  }, [multiUpload, stopCamera]);

  // === Datei-Auswahl ===
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target. files;
    if (!selectedFiles || selectedFiles.length === 0) {
      // Abbruch - zurück zur Auswahl
      debugLog('handleFileSelect: cancel/no files');
      setMode('selection');
      return;
    }

    debugLog('handleFileSelect: files', Array.from(selectedFiles).map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size
    })));
    setError(null);
    const newFiles:  UploadFile[] = [];

    for (const file of Array.from(selectedFiles)) {
      // Typ-Validierung
      if (!allowedTypes.includes(file.type)) {
        setError(`Dateityp "${file.type}" nicht erlaubt.  Erlaubt: ${allowedTypes.join(', ')}`);
        debugLog('handleFileSelect: rejected type', file.type);
        continue;
      }

      // Größen-Validierung (20MB)
      if (file.size > 20 * 1024 * 1024) {
        setError(`Datei "${file.name}" ist zu groß (max.  20 MB)`);
        debugLog('handleFileSelect: rejected size', file.size);
        continue;
      }

      // Base64 lesen
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader. result as string);
        reader.onerror = reject;
        reader. readAsDataURL(file);
      });

      newFiles.push({
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl,
        file,
      });
    }

    if (newFiles.length > 0) {
      if (multiUpload) {
        setFiles(prev => [...prev, ...newFiles]);
      } else {
        setFiles(newFiles.slice(0, 1)); // Nur erste Datei
      }
      debugLog('handleFileSelect: accepted', newFiles.length);
      setMode('preview');
    }

    // Input zurücksetzen
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [allowedTypes, multiUpload]);

  // === Datei entfernen ===
  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    if (files.length <= 1) {
      setMode('selection');
    }
  };

  // === Upload durchführen ===
  const handleUpload = () => {
    if (files.length === 0) return;
    debugLog('handleUpload: start', files.map((f) => ({ name: f.name, type: f.type, size: f.size })));
    onUpload(files);
    handleClose();
  };

  // === Modal schließen ===
  const handleClose = () => {
    stopCamera();
    setFiles([]);
    setMode('selection');
    setError(null);
    onClose();
  };

  // === Kamera starten wenn Mode wechselt ===
  useEffect(() => {
    if (isOpen && mode === 'camera' && !isCameraActive) {
      startCamera();
    }
    return () => {
      if (mode !== 'camera') {
        stopCamera();
      }
    };
  }, [isOpen, mode, isCameraActive, startCamera, stopCamera]);

  // === Cleanup bei Schließen ===
  useEffect(() => {
    if (! isOpen) {
      stopCamera();
    }
  }, [isOpen, stopCamera]);
  useEffect(() => {
    if (isOpen) {
      debugLog('open', {
        mode,
        multiUpload,
        imagesAllowed,
        pdfAllowed,
        acceptString,
        userAgent: navigator.userAgent
      });
    }
  }, [isOpen, mode, multiUpload, imagesAllowed, pdfAllowed, acceptString]);

  // === Hilfsfunktionen ===
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const isPdf = (type: string) => type === 'application/pdf';
  const isImage = (type: string) => type.startsWith('image/');

  if (! isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'preview' ? `${files.length} Datei(en) ausgewählt` : title}
          </h3>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white p-2"
            aria-label="Schließen"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-auto">
          {/* Fehleranzeige */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark: text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* === AUSWAHLMENÜ === */}
          {mode === 'selection' && (
            <div className="flex flex-col space-y-4 py-4">
              {/* Kamera (nur wenn Bilder erlaubt) */}
              {imagesAllowed && (
                <button
                  onClick={() => {
                    debugLog('click: camera button');
                    setMode('camera');
                  }}
                  className="flex items-center justify-center py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h. 93a2 2 0 001.664-. 89l. 812-1.22A2 2 0 0110. 07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Kamera öffnen
                </button>
              )}

              {/* Dateisystem (alle Formate) */}
              <label
                onClick={() => debugLog('click: files label')}
                className="flex items-center justify-center py-4 px-6 bg-gray-100 hover: bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg cursor-pointer transition-colors"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {pdfAllowed ?  'Datei auswählen (PDF, Bilder, andere Formate)' : 'Datei auswählen (Bilder)'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptString}
                  multiple={multiUpload}
                  onClick={() => debugLog('click: files input')}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>

              {/* Multi-Upload Hinweis */}
              {multiUpload && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  Mehrere Dateien können gleichzeitig ausgewählt werden
                </p>
              )}
            </div>
          )}

          {/* === KAMERA === */}
          {mode === 'camera' && (
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* === VORSCHAU === */}
          {mode === 'preview' && files.length > 0 && (
            <div className="space-y-4">
              {files. map((file) => (
                <div
                  key={file.id}
                  className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  {/* Vorschau */}
                  <div className="w-16 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                    {isImage(file.type) ? (
                      <img
                        src={file.dataUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : isPdf(file.type) ? (
                      <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9v6h-1v-2H7v2H6v-6h1v3h2v-3h1zm4 0v1h-1v4h1v1h-3v-1h1v-4h-1v-1h3zm5 0a1 1 0 011 1v4a1 1 0 01-1 1h-2v-6h2z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </div>

                  {/* Dateiinfo */}
                  <div className="ml-4 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file. size)}
                    </p>
                  </div>

                  {/* Entfernen-Button */}
                  <button
                    onClick={() => removeFile(file.id)}
                    className="ml-2 p-2 text-gray-400 hover: text-red-500 transition-colors"
                    aria-label="Entfernen"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-. 867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}

              {/* Weitere Dateien hinzufügen (nur bei Multi-Upload) */}
              {multiUpload && (
                <label className="flex items-center justify-center py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-sm text-gray-600 dark:text-gray-300">Weitere Dateien hinzufügen</span>
                  <input
                    type="file"
                    accept={acceptString}
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Hidden Canvas für Kamera */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Footer / Actions */}
        <div className="p-4 border-t dark:border-gray-700 flex justify-center gap-4">
          {/* Kamera-Modus */}
          {mode === 'camera' && (
            <>
              <button
                onClick={() => {
                  stopCamera();
                  setMode('selection');
                }}
                className="px-4 py-2 bg-gray-200 dark: bg-gray-700 text-gray-900 dark:text-white rounded-lg hover: bg-gray-300 dark:hover: bg-gray-600 transition-colors"
              >
                Zurück
              </button>
              <button
                onClick={takePicture}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h. 93a2 2 0 001.664-. 89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                </svg>
                Aufnehmen
              </button>
            </>
          )}

          {/* Vorschau-Modus */}
          {mode === 'preview' && files.length > 0 && (
            <>
              <button
                onClick={() => {
                  setFiles([]);
                  setMode('selection');
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover: bg-gray-300 dark:hover: bg-gray-600 transition-colors"
              >
                Verwerfen
              </button>
              <button
                onClick={handleUpload}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {files.length > 1 ? `${files.length} Dateien hochladen` : 'Hochladen'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
